import { useEffect, useState } from 'react';

/** Chrome and Edge's "this site can be installed" event (not in TypeScript's DOM types yet). */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// The event can fire before React has drawn anything, so it's caught as soon as this
// module loads and kept until the Settings page asks for it.
let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault(); // Show our button instead of the browser's own banner.
    deferred = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

/** True when running as the installed app rather than in a browser tab. */
export const isInstalledApp = () =>
  typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches ?? false);

/**
 * Whether the browser offers to install the dashboard right now, and a function that
 * shows its install dialog. Browsers without the event (Firefox, Safari) give false.
 */
export function useInstallPrompt(): { canInstall: boolean; install: () => Promise<void> } {
  const [canInstall, setCanInstall] = useState(deferred !== null);
  useEffect(() => {
    const update = () => setCanInstall(deferred !== null);
    listeners.add(update);
    return () => {
      listeners.delete(update);
    };
  }, []);
  return {
    canInstall,
    install: async () => {
      const event = deferred;
      if (!event) return;
      await event.prompt();
      const { outcome } = await event.userChoice;
      // The browser won't offer the same event twice.
      if (outcome === 'accepted') deferred = null;
      notify();
    },
  };
}
